import React, { useState } from "react";
import { array, node, string, object } from "prop-types";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "./utils";
import { t } from "@/i18n";

const ErrorFallback = ({ error, className }) => {
  const [expanded, setExpanded] = useState(false);

  const message = error?.message || String(error);

  return (
    <div
      role="alert"
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center",
        className,
      )}
    >
      <div className="bg-destructive/10 flex h-24 w-24 items-center justify-center rounded-full">
        <TriangleAlert className="text-destructive h-12 w-12" />
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold">{t("errorBoundary.title")}</h3>
        <p className="text-muted-foreground">
          {t("errorBoundary.description")}
        </p>
      </div>
      {message && <p className="text-destructive break-words">{message}</p>}
      {error?.stack && (
        <>
          <Button
            variant="link"
            size="sm"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            {expanded
              ? t("errorBoundary.hide_details")
              : t("errorBoundary.show_details")}
          </Button>
          {expanded && (
            <pre className="text-muted-foreground p-md w-full overflow-auto rounded-lg border border-slate-300 text-left whitespace-pre-wrap">
              {error.stack}
            </pre>
          )}
        </>
      )}
    </div>
  );
};

ErrorFallback.propTypes = {
  error: object,
  className: string,
};

const ErrorBoundary = ({ children, className, resetKeys }) => (
  <ReactErrorBoundary
    resetKeys={resetKeys}
    fallbackRender={({ error }) => (
      <ErrorFallback error={error} className={className} />
    )}
  >
    {children}
  </ReactErrorBoundary>
);

ErrorBoundary.propTypes = {
  children: node,
  className: string,
  resetKeys: array,
};

export default ErrorBoundary;
